import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Crown, X, Sparkles } from "lucide-react";
import client from "../lib/api";

const PROMO_CODE = "WELCOME30";
const STORAGE_KEY = "sm_dismiss_promo30";

/** Sticky banner shown when a free user hits the daily-like limit.
 *  Encourages Premium subscription with a 30% off coupon (validated against Stripe).
 */
const PromoBanner = ({ trigger = false, onSubscribe }) => {
  const navigate = useNavigate();
  const [coupon, setCoupon] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem(STORAGE_KEY) === "1") setDismissed(true);
  }, []);

  useEffect(() => {
    if (!trigger || coupon) return;
    let on = true;
    (async () => {
      try {
        const r = await client.get(`/coupons/${PROMO_CODE}`);
        if (on) setCoupon(r.data);
      } catch (err) {
        // The promo banner is a soft sell — we don't surface fetch errors
        // to the user. Logged so failures show up in DevTools / Sentry.
        // eslint-disable-next-line no-console
        console.warn("Failed to fetch promo coupon:", err);
      }
    })();
    return () => { on = false; };
  }, [trigger, coupon]);

  if (!trigger || dismissed || !coupon || !coupon.valid) return null;

  const dismiss = () => { sessionStorage.setItem(STORAGE_KEY, "1"); setDismissed(true); };
  const goPremium = () => {
    if (onSubscribe) onSubscribe(coupon.code);
    else navigate(`/premium?coupon=${encodeURIComponent(coupon.code)}`);
  };

  return (
    <div className="fixed inset-x-0 bottom-0 z-40 px-4 pb-4" data-testid="promo-banner">
      <div className="max-w-5xl mx-auto rounded-2xl bg-gradient-to-r from-[#C5A880]/95 to-[#9C7B4A]/95 backdrop-blur-md text-[#0C0E12] shadow-2xl border border-[#C5A880] p-4 md:p-5 flex flex-col md:flex-row items-start md:items-center gap-4">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="w-10 h-10 rounded-full bg-[#0C0E12]/15 flex items-center justify-center shrink-0">
            <Crown className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <div className="font-display text-lg leading-tight">
              {coupon.percent_off ? `${Math.round(coupon.percent_off)}% off no primeiro mês` : "Oferta Premium"}
            </div>
            <p className="text-xs opacity-80 mt-0.5">
              {coupon.name || "Subscreve agora e desbloqueia análise IA, validação e likes ilimitados."}
              {" · "}<span className="font-mono">{coupon.code}</span>
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 ml-auto">
          <button onClick={goPremium}
            className="bg-[#0C0E12] text-[#C5A880] px-4 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-2 hover:bg-[#161920] transition-colors"
            data-testid="promo-cta">
            <Sparkles className="w-4 h-4" /> Subscrever com desconto
          </button>
          <button onClick={dismiss} className="text-[#0C0E12]/60 hover:text-[#0C0E12]"
            aria-label="Fechar"
            data-testid="promo-dismiss">
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default PromoBanner;
